import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { ManageReferralsModal } from '../../components/modal/app_modals/manageReferralsModal'
import { OptionSelectModal } from '../../components/modal/app_modals/optionSelectModal'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { formatCurrency, formatNumber } from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useDownloadReferralReportsMutation,
  useExportReferralReportsMutation,
  useGetReferralAnalyticsQuery,
  useGetReferralReportQuery,
} from '../../redux-toolkits/loyalty/loyalty.slice'
import {
  ReferralReportQuery,
  ReferralSortBy,
} from '../../redux-toolkits/loyalty/loyalty.type'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import { Sort } from '../../redux-toolkits/shared/shared.type'
const referralFilterOptions: {
  name: string
  value: {
    sortBy: ReferralSortBy
    sortOrder: Sort
  }
}[] = [
  {
    name: 'New user first purchases low to high',
    value: {
      sortBy: 'newUserFirstPurchases',
      sortOrder: 'asc',
    },
  },
  {
    name: 'New user first purchases high to tow',
    value: {
      sortBy: 'newUserFirstPurchases',
      sortOrder: 'desc',
    },
  },
  {
    name: 'New user sign ups low to high',
    value: {
      sortBy: 'newUserSignUps',
      sortOrder: 'asc',
    },
  },
  {
    name: 'New user sign ups high to low',
    value: {
      sortBy: 'newUserSignUps',
      sortOrder: 'desc',
    },
  },
  {
    name: 'Referral earnings low to high',
    value: {
      sortBy: 'referrerEarnings',
      sortOrder: 'asc',
    },
  },
  {
    name: 'Referral earnings high to low',
    value: {
      sortBy: 'referrerEarnings',
      sortOrder: 'desc',
    },
  },
  {
    name: 'Total order value low to high',
    value: {
      sortBy: 'totalOrderValue',
      sortOrder: 'asc',
    },
  },
  {
    name: 'Total order value high to low',
    value: {
      sortBy: 'totalOrderValue',
      sortOrder: 'desc',
    },
  },
  {
    name: 'Total orders from new users low to high',
    value: {
      sortBy: 'totalOrdersFromNewUsers',
      sortOrder: 'asc',
    },
  },
  {
    name: 'Total orders from new users high to low',
    value: {
      sortBy: 'totalOrdersFromNewUsers',
      sortOrder: 'desc',
    },
  },
]

export const Referrals = () => {
  const [query, setQuery] = useState<ReferralReportQuery>({
    page: 1,
    limit: NUMBER_OF_ITEMS_PER_PAGE,
    search: undefined,
    sortBy: 'totalOrderValue',
    sortOrder: 'asc',
  })

  const [searchParams, setSearchParams] = useSearchParams()

  const { user } = useAppSelector((state) => state.auth)

  const { modalOpen, modal, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const { data: referralMetrics, isFetching: fetchingReferralMetrics } =
    useGetReferralAnalyticsQuery(
      query
        ? {
            startDate: query.startDate,
            endDate: query.endDate,
            filter: query.filter,
            isCustomDateRange: query.isCustomDateRange,
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const { data, isFetching } = useGetReferralReportQuery(
    query ? { ...query } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [exportReferrals, { isLoading: exportingReferrals }] =
    useExportReferralReportsMutation()

  const [downloadReferrals, { isLoading: downloadingReferrals }] =
    useDownloadReferralReportsMutation()

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
      sortBy:
        (searchParams.get('sortBy') as ReferralSortBy) || 'totalOrderValue',
      sortOrder: (searchParams.get('sortOrder') as Sort) || 'asc',
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) ||
          'inception_to_date',
    })
  }, [searchParams])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <PageHeader title="Referrals" className="!my-0" />

        <Button
          label="Manage rewards"
          primary
          onClick={() => {
            handleModalOpen('manageReferralsModal')
          }}
        />
      </div>

      <DateFilterNav
        filter={query?.filter ?? 'custom'}
        isCustomDateRange={query?.isCustomDateRange ?? false}
        startDate={query?.startDate}
        endDate={query?.endDate}
        setFilter={(filter) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            filter: filter.filter ?? '',
            isCustomDateRange: String(filter.isCustomDateRange),
            endDate: filter.endDate ?? '',
            startDate: filter.startDate ?? '',
            skip: '0',
          })
        }}
      />

      <CountCardContainer className="!my-0">
        <CountCard
          count={referralMetrics?.data?.totalSignups ?? 0}
          text="Total Sign-ups"
          isCurrency={false}
          loading={fetchingReferralMetrics}
        />

        <CountCard
          count={referralMetrics?.data?.totalFirstPurchases ?? 0}
          text="Total First Purchase"
          isCurrency={false}
          loading={fetchingReferralMetrics}
          valueSuffix={`(${
            (referralMetrics?.data?.signupToPurchaseRate ?? 0) * 100
          }% Conv.)`}
        />

        <CountCard
          count={referralMetrics?.data?.totalPurchases ?? 0}
          text="Total Referees Purchases"
          isCurrency={false}
          loading={fetchingReferralMetrics}
        />

        <CountCard
          count={referralMetrics?.data?.totalReferralOrderValue ?? 0}
          text="Total Order Value"
          isCurrency
          loading={fetchingReferralMetrics}
        />

        <CountCard
          count={referralMetrics?.data?.totalReferralRewards ?? 0}
          text="Total Referral Rewards "
          isCurrency
          loading={fetchingReferralMetrics}
        />
      </CountCardContainer>

      <div className="flex-wrap flex items-center gap-4 justify-between">
        <SearchInput
          placeholder="Search"
          searchFunction={(val) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              search: val,
              page: '1',
            })
          }}
          className="!w-full md:!max-w-[400px]"
          initialValue={searchParams.get('search') || undefined}
        />

        <div className="flex ml-auto flex-wrap gap-6 justify-end items-center">
          <Dropdown
            menuButton={
              <Button
                label={`Sort by: ${referralFilterOptions.find(
                  (opt) => opt.value.sortBy === query.sortBy,
                )?.name}`}
                primary={false}
                onClick={() => {}}
                icon="filter"
                className="!font-normal !max-w-[250px]"
                labelClassName="whitespace-nowrap !text-ellipsis !line-clamp-1"
              />
            }
            menuItems={referralFilterOptions.map((opt) => {
              const selected =
                opt.value.sortBy === query.sortBy &&
                opt.value.sortOrder === query.sortOrder
              return {
                ...opt,
                name: `${opt.name} ${selected ? '✔️' : ''}`,
                onClick: () => {
                  setSearchParams({
                    ...Object.fromEntries(searchParams),
                    ...opt.value,
                  })
                },
                className: selected ? '!text-black !font-medium' : '',
              }
            })}
            menuItemClassName="border-b-0"
            menuClassName="!w-[350px]"
          />

          <Button
            label="Export"
            primary
            onClick={() => {
              handleModalOpen('optionSelectModal')
            }}
          />
        </div>
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'REFERRER NAME',
            'NEW USER SIGN-UPS',
            'TOTAL FIRST PURCHASES',
            'TOTAL ORDERS FROM NEW USERS',
            'TOTAL ORDER VALUE',
            'REFERRER EARNINGS',
          ]}
          isEmpty={[0, undefined].includes(data?.data?.docs?.length)}
          rows={
            data?.data?.docs?.map((item, idx) => {
              return {
                id: idx,
                content: [
                  <span className="!capitalize" key={`referrer-${idx}`}>
                    {item?.referrerName ?? 'N/A'}
                  </span>,
                  formatNumber(item.newUserSignUps ?? ''),
                  formatNumber(item.newUserFirstPurchases ?? ''),
                  formatNumber(item.totalOrdersFromNewUsers ?? ''),
                  formatCurrency(item.totalOrderValue),
                  formatCurrency(item.referrerEarnings),
                ],
              }
            }) ?? []
          }
          loading={isFetching}
          currentPage={Number(query.page)}
          totalDataCount={data?.data?.total}
          pageLimit={Number(query.limit)}
          onPageChange={(page) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: `${page}`,
            })
          }}
        />
      </div>

      <ManageReferralsModal
        open={modalOpen && modal === 'manageReferralsModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
      />

      <OptionSelectModal
        open={modalOpen && modal === 'optionSelectModal'}
        setOpen={setModalOpen}
        title="Export Referrals"
        onCloseCallback={() => {
          handleModalClose()
        }}
        options={[
          {
            title: 'Export via email',
            onClick: async () => {
              const resp = await exportReferrals({
                ...query,
                email: user?.email ?? '',
                skip: undefined,
                page: undefined,
                limit: undefined,
              })

              if (resp.data?.success) {
                handleModalClose()
              }
            },
            loading: exportingReferrals,
          },
          {
            title: 'Download report',
            onClick: async () => {
              const resp = await downloadReferrals({
                ...query,
                skip: undefined,
                limit: undefined,
              })

              if (resp.data?.success) {
                handleModalClose()
              }
            },
            loading: downloadingReferrals,
          },
        ]}
      />
    </div>
  )
}
